import React, { memo, useContext, useEffect, useState } from 'react';
import { Label, Error, Input } from 'components/Form'
import { MenuItem, Chip, createFilterOptions, Typography, Tooltip, SvgIcon, Radio } from '@mui/material'
import { AutoCompleteStyles, WrapAutocomplete, WrapField, AutoCompleteOptions, WrapFieldContent, WrapMoreOptions, TextFieldStyles } from './Styled'
import { isEqual, debounce, isObject } from 'lodash';
import { white } from 'styles/theme';
import { AbilityContext } from 'components/Permissions/Can';
import { isArray } from 'lodash-contrib';
import { ReactComponent as PulldownIcon } from 'assets/icons/Pulldown.svg';
import SelectOption from './SelectOption';

const defaultFilterOptions = createFilterOptions();

const EMPTY_ARRAY = [];

/**
 * AutoComplete Component (Wrapper מעל Material UI)
 * קומפוננטה מרכזית וחכמה לבחירת ערכים מרשימה (Dropdown).
 * תומכת בחיפוש בשרת (עם Debounce), סינון מקומי מתקדם במספר שדות במקביל, 
 * בחירה מרובה (Multiple), בדיקת הרשאות למשתמש (AbilityContext) ותצוגה מותאמת.
 * * @param {Object} props
 * @param {Object} props.input - אובייקט חובה (לרוב מספריות ניהול טפסים) המכיל `value` ו-`onChange`
 * @param {Array} props.options - מערך האופציות שיוצגו ברשימה
 * @param {boolean} [props.multiple] - האם לאפשר בחירת מספר ערכים (תומך במערכים)
 * @param {Function} [props.onSearchChange] - פונקציה לביצוע חיפוש אסינכרוני מול השרת
 * @param {Array<string>} [props.searchFields] - שדות לסינון מקומי (למשל: ['name', 'id'])
 * @param {number} [props.optionsLimit] - הגבלת מספר התוצאות שיוצגו ברשימה הנגללת
 * @param {string} [props.labelKey="name"] - המפתח באובייקט שממנו יישלף הטקסט לתצוגה
 * @param {boolean} [props.isLocked] - אם true, מרנדר שדה Input בסיסי נעול במקום את ה-AutoComplete
 */
const AutoComplete = (props) => {
    const
        { readOnlyInput, renderCustomInput, showBlurred, loading, searchFields, popupIcon, loadingText, onClearSearch, onSearchChange, paperComponent: paperComponent, hideClear, isLocked, blurred, subText, optionsLimit, open, light, focus, shadow, badge, borderRadius, name, minWidth, required, border, gridArea, isOptionEqualToValue, arrayIndex, fullWidth, noOptionsText, systemUI, createNew, label, width, getOptionLabel, tooltipKey, labelKey = "name", input, options = [], disabled, className, placeholder, multiple, mandatory } = props,
        { onChange, value } = input;

    const ability = useContext(AbilityContext)

    const [focusInput, setFocusInput] = useState(false)
    const [openMenu, setOpenMenu] = useState(open || false);
    const [inputValue, setInputValue] = useState('');

    // פונקציית חיפוש עם Debounce כדי לא להעמיס על השרת
    const debouncedSearch = React.useRef(
        debounce((nextValue) => {
            // אנחנו שולחים את החיפוש בכל מקרה, גם אם זו אות אחת או מחרוזת ריקה
            if (onSearchChange) {
                onSearchChange(nextValue);
            }
        }, 500)
    ).current;

    const handleInputChange = (event, newInputValue, reason) => {
        // אם המשתמש בחר אופציה (או לחץ מחוץ לשדה), במצב multiple נרצה לנקות את שורת החיפוש
        if (reason === 'reset') {
            setInputValue(multiple ? '' : newInputValue);
            return;
        }

        setInputValue(newInputValue);

        if (reason === 'input') {
            debouncedSearch(newInputValue);
        }

        if (reason === 'clear') {
            debouncedSearch.cancel();
            if (onSearchChange) {
                onSearchChange('');
            }
        }
    };

    const renderValue = () => {
        if (value == undefined) return multiple ? EMPTY_ARRAY : null;

        if (value && typeof value != 'object') {
            const optionIndex = options?.findIndex((opt) => opt?.id.toString() === value?.toString() || opt?.name?.toString() === value?.toString())
            return options?.[optionIndex] || ""
        }
        else if (value && !multiple) return value;
        else if (multiple) {
            return isArray(value) ? value : EMPTY_ARRAY; // שימוש במערך הקבוע
        }
        else return value || null;
    }

    const filterOptions = (options, state) => {
        // 1. אם יש חיפוש שרת, הלקוח לא מסנן כלום
        if (onSearchChange) {
            return options;
        }

        // 2. לוגיקת חיפוש מרובה פרמטרים בצד לקוח
        const searchStr = state.inputValue.toLowerCase();

        // אם אין שדות מיוחדים המוגדרים עבור החיפוש
        if (searchFields == null || searchFields.length == 0) {
            const filteredOptions = defaultFilterOptions(options, state).slice(0, optionsLimit);
            const moreOptions = defaultFilterOptions(options, state).length - optionsLimit;
            return moreOptions > 0 ? filteredOptions.concat({ moreOptions: `${moreOptions} תוצאות נוספות` }) : filteredOptions;
        }

        // סינון לפי מספר שדות במקביל (למשל name ו-id)
        const customFiltered = options.filter((option) => {
            // some מחזיר true אם לפחות שדה אחד מכיל את מילת החיפוש
            return searchFields?.some((field) => {
                // שומרים את הערך כדי לא לבצע פעולות על undefined/null
                const fieldValue = option?.[field];

                // אם אין ערך בשדה הזה, נדלג לשדה הבא
                if (fieldValue == null) return false;

                // הופכים למחרוזת (וגם לאותיות קטנות כדי שהחיפוש לא יהיה רגיש לאותיות גדולות/קטנות באנגלית)
                return fieldValue.toString().toLowerCase().includes(searchStr.toLowerCase());
            });
        });

        // הלוגיקה שלך לחיתוך והצגת "תוצאות נוספות"
        const finalFiltered = customFiltered.slice(0, optionsLimit);
        const moreOptionsCount = customFiltered.length - optionsLimit;

        return moreOptionsCount > 0
            ? finalFiltered.concat({ moreOptions: `${moreOptionsCount} תוצאות נוספות` })
            : finalFiltered;
    }

    // 1. איך להציג את הבחירה בתוך ה-Input (במקום [object Object])
    const getLabel = (opt) => {
        return getOptionLabel ? getOptionLabel(opt) : opt?.[labelKey] || "";
    }
    // const getLabel = (opt) => getOptionLabel ? getOptionLabel(opt) : opt?.[labelKey]
    const getTooltip = (opt) => props.getTooltip ? props.getTooltip?.(opt) : opt?.[tooltipKey]

    const abilityOption = (option) => {
        if (option?.role)
            if (ability.can(option.role, option.feature))
                return true
            else return false
        return true
    }

    const handleClose = (event, reason) => {
        setOpenMenu(false);

        // כאן הלוגיקה החדשה:
        // אנחנו מנקים את החיפוש בשרת רק אם המשתמש עזב את השדה (blur) 
        // או ביטל את הפעולה (escape).
        // if (reason === 'blur' || reason === 'escape') {
        //     if (onClearSearch) {
        //         onClearSearch(); // מנקה את ה-State בשרת (testTakerSearchText)
        //     }

        //     // מחזירים את הטקסט בתיבה לערך שנבחר באמת (אם יש כזה)
        //     // זה ינקה את "יפה" אם לא נבחרה שום "יפה" מהרשימה
        //     // setInputValue(input.value ? getLabel(input.value) : '');
        // }

        // אם הסיבה היא 'selectOption', אנחנו לא מריצים את הניקוי הידני
        // כי MUI כבר ידאג לעדכן את ה-inputValue לערך שנבחר.
    };

    useEffect(() => {
        setFocusInput(focus)
    }, [focus])

    return (
        isLocked ? <Input {...props} /> :
            <WrapField className={className} gridArea={gridArea && `${gridArea}_${arrayIndex}`}>
                <AutoCompleteStyles
                    showBlurred={showBlurred}
                    labelWidth={width}
                >
                    <WrapFieldContent>
                        {label && <Label label={label} mandatory={mandatory} />}
                        <WrapAutocomplete
                            inputValue={inputValue}
                            onInputChange={handleInputChange}
                            disableClearable={hideClear}
                            light={light}
                            shadow={shadow}
                            borderRadius={borderRadius}
                            border={border}
                            open={openMenu} // חיבור ה-State
                            onOpen={() => setOpenMenu(true)}
                            onClose={handleClose}
                            $systemUI={systemUI}
                            minWidth={minWidth}
                            focus={focusInput && !value}
                            filterOptions={filterOptions}
                            id="combo-box-demo"
                            loading={loading}
                            loadingText={loadingText || 'טוען נתונים...'}
                            popupIcon={popupIcon || <SvgIcon className='pulldown-icon' size="small" fontSize='10' component={PulldownIcon} inheritViewBox />}
                            color="thirdly"
                            isOptionEqualToValue={(option, val) => {
                                if (isOptionEqualToValue)
                                    isOptionEqualToValue(option, val)

                                else {
                                    // אם val הוא אובייקט (כי MUI מעביר לפעמים את האובייקט מה-value)
                                    const valId = typeof val === 'object' ? val?.id : val;
                                    const optionId = option?.id;

                                    return optionId === valId;
                                }
                            }}
                            disableCloseOnSelect={multiple}
                            noOptionsText={noOptionsText || 'אין תוצאות'}
                            options={options || []}
                            sx={{ width: `${value ? width + 30 : width}px` }}
                            disabled={disabled}
                            getOptionLabel={getLabel}
                            multiple={multiple}
                            value={renderValue()}
                            onChange={(event, newValue) => {
                                onChange(newValue);
                                props.onChange && props.onChange(newValue)
                            }}
                            PaperComponent={(paperProps) => {
                                const { children, ...rest } = paperProps;
                                return (
                                    <AutoCompleteOptions
                                        {...rest} // חובה להעביר את rest! זה כולל את ה-Events ש-MUI צריך
                                        style={{ ...rest.style, backgroundColor: white, borderRadius: '10px' }}
                                    >
                                        {paperComponent ? (
                                            <props.paperComponent
                                                value={value}
                                                onChange={onChange}
                                                isOptionEqualToValue={isOptionEqualToValue}
                                                onClose={() => setOpenMenu(false)}
                                                multiple={multiple}
                                            />
                                        ) : children}
                                    </AutoCompleteOptions>
                                );
                            }}
                            renderTags={(value, getTagProps) => {
                                const firstLabel = getLabel(value[0]);
                                const extraCount = value.length - 1;

                                // כשיש יותר מבחירה אחת הטקסט נחתך (+X), הטולטיפ מציג את כל הבחירות במלואן
                                const fullSelectionLabel = value.map(getLabel).join(', ');

                                return (
                                    <Tooltip title={fullSelectionLabel} placement="top">
                                        <div className={`selected-label ${showBlurred && 'blurred2'}`}>
                                            {/* הטקסט הראשי נחתך בשלוש נקודות; ה-+X לא נחתך לעולם, כדי שתמיד יישאר גלוי */}
                                            <span className='selected-label-text'>{firstLabel}</span>
                                            {extraCount > 0 && <span className='selected-label-extra'>{`+${extraCount}`}</span>}
                                        </div>
                                    </Tooltip>
                                )
                            }}
                            renderInput={(params) => {
                                // אם העברת Input חיצוני, נשתמש בו ונעביר לו את ה-params של MUI
                                if (renderCustomInput) {
                                    return renderCustomInput(params);
                                }

                                // ברירת המחדל הקיימת שלך (נשארת בדיוק אותו דבר)
                                return (
                                    <TextFieldStyles
                                        {...params}
                                        showBlurred={showBlurred}
                                        onClick={() => setFocusInput(false)}
                                        badge={badge}
                                        inputProps={{
                                            ...params.inputProps,
                                            readOnly: readOnlyInput
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {params.InputProps?.endAdornment}
                                                </>
                                            )
                                        }}
                                        color="thirdly"
                                        size='small'
                                        variant="outlined"
                                        label={`${required ? "*" : ""}${placeholder}`}
                                    />
                                );
                            }}
                            renderOption={(optionProps, option, { index, selected }) => {
                                return (
                                    abilityOption(option) &&
                                    <React.Fragment key={index}>
                                        {option?.moreOptions ? (
                                            <WrapMoreOptions>
                                                <Typography variant="body2" color="textSecondary">
                                                    {option.moreOptions}
                                                </Typography>
                                            </WrapMoreOptions>
                                        ) :
                                            <>
                                                {
                                                    option?.createNew ?
                                                        <MenuItem onClick={() => createNew.action()}>
                                                            <b> {createNew.label}</b>
                                                        </MenuItem>
                                                        :
                                                        <SelectOption
                                                            showBlurred={showBlurred}
                                                            option={option}
                                                            optionProps={optionProps}
                                                            selected={selected}
                                                            fullWidth={fullWidth}
                                                            width={width}
                                                            multiple={multiple}
                                                            index={index}
                                                            blurred={blurred}
                                                            getLabel={getLabel}
                                                            getTooltip={getTooltip}
                                                        />
                                                }
                                            </>
                                        }
                                    </React.Fragment>
                                )
                            }}
                        />
                    </WrapFieldContent>
                    {subText && <div className='sub-text-field'>{subText}</div>}
                    <Error {...props} />
                </AutoCompleteStyles>
            </WrapField>
    );
}

/**
 * פונקציית Memo לבדיקת רינדור מחדש.
 * משווה את הפרופס המרכזיים כדי למנוע רינדורים עודפים מיותרים של הרכיב הכבד הזה.
 */
const isRender = (prev, next) => {
    const render =
        !isEqual(prev.options, next.options) ||
        !isEqual(prev.input, next.input) ||
        !isEqual(prev.touched, next.touched) ||
        !isEqual(prev.error, next.error) ||
        !isEqual(prev.disabled, next.disabled) ||
        !isEqual(prev.focus, next.focus) ||
        !isEqual(prev.loading, next.loading)
    return !render
}

export default memo(AutoComplete, isRender)